import React, { useRef, useState, useEffect, useMemo } from "react";
import { FormattedMessage } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import classnames from "classnames";
import {
  Controller,
  FormProvider,
  useForm,
  useFieldArray,
} from "react-hook-form";
import { isEmpty } from "lodash";
import Select, { components } from "react-select";
import {
  TextInput,
  RadioButtonGroup,
  Blockquote,
  RadioButton,
  Input,
  InlineLoading,
} from "@wfp/ui";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faWheatAwn } from "@fortawesome/free-solid-svg-icons";
import moment from "moment";

import { RepeaterItem } from "components/Repeater";
import RepeaterAddButton from "components/RepeaterAddButton";
import CommoditySelect from "components/CommoditySelect";
import DateInput from "components/DateInput";
import Empty from "components/Empty";
import ModalExtended from "components/ModalExtended";
import QuantityInput from "components/QuantityInput";
import ErrorList from "components/ErrorList";
import Camera from "components/Camera";

import incidentCausesDuck from "data-handler/ducks/incidentCauses";
import useStore from "helpers/useStore";
import {
  deliveryCategory,
  purchaseDetailCategory,
  selectContainerStyles,
  takeHomeRationCategory,
} from "SCConstants";

import "./_incidents-edit.scss";

import {
  getAllStores,
  checkIncidentDateIsUniquePerDate,
  getCurrentSchoolYear,
  getPreviousSchoolYears,
  SchoolYear,
  Store,
  IncidentEvent,
  NewStockEvent,
  MeasureUnit,
} from "data-handler/ducks/stores";

import {
  deliveriesInStockByCommodityByCategory,
  getDeliveryByCommodityAndDeliveryID,
  getPurchaseByCommodityPurchaseID,
  getStockStatus,
} from "helpers/stock";

import {
  clearDeliveryOptions,
  getDeliveries,
  setDeliveries,
} from "data-handler/ducks/deliveries";

import {
  getCurrentSchoolProfile,
  requestAllCommoditySources,
  getAllCommoditySources,
  getAllCommoditySourcesFetching,
  getCurrentSchoolProfileWfpCommodities,
  getCurrentSchoolProfileCommodities,
} from "data-handler/ducks/schools";

import { getVendorCategories } from "data-handler/ducks/vendors";
import { getSelectedVendorName } from "components/CashToSchoolShow";
import { getReportByYearMonthDay } from "data-handler/ducks/reportsV3";
import { RootState } from "data-handler/rootReducer";
import { getCategory } from "helpers/category";
import { getIsAdmin } from "helpers/users";
import { CommoditySource } from "data-handler/ducks/schools2";
import { getLoggedInOffline, getCurrentUser } from "data-handler/ducks/auth";
import MeasureUnitValue from "components/MeasureUnitValue";

export const isReportClosedOrRejected = (
  state: RootState,
  selectedDate: string
) => {
  const splitDate = selectedDate?.split("-");
  const year = splitDate ? parseInt(splitDate[0]) : undefined;
  const month = splitDate ? parseInt(splitDate[1]) : undefined;
  const day = splitDate ? parseInt(splitDate[2]) : undefined;
  const reportBySelectedDate = getReportByYearMonthDay(
    state,
    year!,
    month!,
    day!
  );

  return reportBySelectedDate
    ? reportBySelectedDate.state === "closed" ||
        reportBySelectedDate.state === "rejected"
    : undefined;
};

export const isReportApprovedOrSubmitted = (
  state: RootState,
  selectedDate: string
) => {
  const splitDate = selectedDate?.split("-");
  const year = splitDate ? parseInt(splitDate[0]) : undefined;
  const month = splitDate ? parseInt(splitDate[1]) : undefined;
  const day = splitDate ? parseInt(splitDate[2]) : undefined;
  const reportBySelectedDate = getReportByYearMonthDay(
    state,
    year!,
    month!,
    day!
  );

  return reportBySelectedDate
    ? reportBySelectedDate.state === "approved" ||
        reportBySelectedDate.state === "submitted"
    : undefined;
};

export const schoolYearDateValidation = (
  date: string,
  currentYearStore: SchoolYear,
  previousSchoolYears: SchoolYear[]
) => {
  let isSelectedDateInSchoolYear = true;
  const previousSchoolYearsStartsOn =
    previousSchoolYears?.length > 0
      ? previousSchoolYears.map(({ starts_on, ends_on }) => ({
          starts_on,
          ends_on,
        }))
      : undefined;

  if (
    previousSchoolYearsStartsOn &&
    previousSchoolYearsStartsOn.length > 0 &&
    date &&
    !currentYearStore
  ) {
    isSelectedDateInSchoolYear = !!previousSchoolYearsStartsOn.find((item) => {
      return (
        moment(date).isBetween(item?.starts_on, item?.ends_on) ||
        moment(date).isSame(item?.starts_on) ||
        moment(date).isSame(item?.ends_on)
      );
    });
  } else if (currentYearStore) {
    isSelectedDateInSchoolYear =
      moment(date).isBetween(
        currentYearStore?.starts_on,
        currentYearStore?.ends_on
      ) ||
      moment(date).isSame(currentYearStore?.starts_on) ||
      moment(date).isSame(currentYearStore?.ends_on);
  }
  return isSelectedDateInSchoolYear;
};

export const currentSchoolYearDateValidation = (
  currentYearStore: SchoolYear,
  previousSchoolYears?: SchoolYear[]
) => {
  let minDate;
  let maxDate;
  const todaysDate = moment().format("YYYY-MM-DD");
  if (currentYearStore) {
    minDate = currentYearStore?.starts_on;
    maxDate = moment(todaysDate).isSameOrBefore(currentYearStore?.ends_on)
      ? todaysDate
      : currentYearStore?.ends_on;
  } else if (!currentYearStore && previousSchoolYears) {
    minDate = previousSchoolYears[0]?.starts_on;
    maxDate = previousSchoolYears[0]?.ends_on;
  } else {
    minDate = undefined;
    maxDate = moment().format("YYYY-MM-DD");
  }
  return {
    min: minDate,
    max: maxDate,
  };
};

export const ERROR_MESSAGES_INCIDENTS = {
  causesSome: (
    <FormattedMessage
      id="IncidentEditError.causesSome"
      defaultMessage="Please select at least one reason or enter a custom reason"
    />
  ),
  missingDate: (
    <FormattedMessage
      id="IncidentEditError.missingDate"
      defaultMessage="Enter incident date"
    />
  ),
  missingCommodity: (
    <FormattedMessage
      id="IncidentEditError.missingCommodity"
      defaultMessage="Select a commodity"
    />
  ),
  missingQuantity: (
    <FormattedMessage
      id="IncidentEditError.missingQuantity"
      defaultMessage="Enter a quantity"
    />
  ),
  missingIncidentType: (
    <FormattedMessage
      id="IncidentEditError.missingIncidentType"
      defaultMessage="Enter a incident type"
    />
  ),
  incorrectIncidentType: (
    <FormattedMessage
      id="IncidentEditError.incorrectIncidentType"
      defaultMessage="You can't select Return to WFP when you have selected a purchased commodity "
    />
  ),
  missingWaybill: (
    <FormattedMessage
      id="IncidentEditError.missingWaybill"
      defaultMessage="Enter a number here"
    />
  ),
  duplicateWaybill: (
    <FormattedMessage
      id="IncidentEditError.duplicateWaybill"
      defaultMessage="This waybill is already used by another row for the same commodity, please select a different one"
    />
  ),
  duplicatePurchase: (
    <FormattedMessage
      id="IncidentEditError.duplicatePurchase"
      defaultMessage="This purchase is already used by another row for the same commodity, please select a different one"
    />
  ),
  insufficientStock: (
    <FormattedMessage
      id="MealsEdit.insufficientStock"
      defaultMessage="The meal quantity exceeds the stock for this commodity, please reduce."
    />
  ),
  missingCategory: (
    <FormattedMessage
      id="IncidentEditError.selectCategory"
      defaultMessage="Select category"
    />
  ),
  incorrectReason: (
    <FormattedMessage
      id="IncidentEditError.incorrectReason"
      defaultMessage="You cannot select a purchased commodity and return to WFP as a reason"
    />
  ),
  invalidDate: (
    <FormattedMessage
      id="IncidentEditError.date-not-valid"
      defaultMessage="Selected date is not in a school year, please change date"
    />
  ),
  reportClosedOrRejected: (
    <FormattedMessage
      id="IncidentEditError.report-closed-rejected"
      defaultMessage="The report of your chosen date is already closed or rejected. Either reopen the report or change the selected date."
    />
  ),
  reportApprovedOrSubmitted: (
    <FormattedMessage
      id="IncidentEditError.report-approved-submitted"
      defaultMessage="The report of your chosen date is already submitted. Please change the date or contact your school feeding focal point to reopen the report"
    />
  ),
};

// This needs to corrospond to the report functions that check for loss / return.
// Example: report.js:142. This checks is incident is 1 / loss or 2 / return
export const incidentTypes: { [id: string]: React.ReactNode } = {
  1: <FormattedMessage id="IncidentEdit.loss" defaultMessage="Loss" />,
  2: (
    <FormattedMessage
      id="IncidentEdit.returnToWfp"
      defaultMessage="Food Will be returned to WFP"
    />
  ),
};

const makeNewCommodity = (category?: string) => ({
  category: category || deliveryCategory,
  issues: [],
});

const extractValue = (
  v: string | number | { value: string | number } | undefined | null
): string | undefined => {
  if (v === undefined || v === null) return undefined;
  if (typeof v === "object" && "value" in v) {
    const { value } = v;
    return value === undefined || value === null ? undefined : value.toString();
  }
  return v.toString();
};

/**
 * The subset of a form row that identifies which stock line it draws from.
 * A row points either at a delivery/take-home-ration line (`waybill_no`) or at
 * a purchase line (`purchase_commodity_id`), never at both.
 */
export type StockSourceRow = {
  commodity?: number | string;
  category?: string;
  waybill_no?: string | number | { value: string | number };
  purchase_commodity_id?: string | number | { value: string | number };
};

const rowCategory = (row: StockSourceRow) => row.category || deliveryCategory;

export const stockSourceFieldName = (category?: string) =>
  category === purchaseDetailCategory
    ? ("purchase_commodity_id" as const)
    : ("waybill_no" as const);

export const selectedStockSourceId = (
  row: StockSourceRow | undefined
): string | undefined =>
  row ? extractValue(row[stockSourceFieldName(rowCategory(row))]) : undefined;

/**
 * The same commodity may be reported more than once in a single incident, as
 * long as every row draws from a different waybill / purchase. A row may
 * therefore only offer the stock lines that its siblings on the same commodity
 * and the same category have left free.
 */
export const stockSourceIdsTakenByOtherRows = (
  stockEvents: StockSourceRow[] | undefined,
  index: number
): Set<string> => {
  const rows = stockEvents || [];
  const currentRow = rows[index];
  const taken = new Set<string>();
  if (!currentRow?.commodity) return taken;

  rows.forEach((row, rowIndex) => {
    if (rowIndex === index || !row) return;
    if (row.commodity?.toString() !== currentRow.commodity?.toString()) return;
    if (rowCategory(row) !== rowCategory(currentRow)) return;

    const sourceId = selectedStockSourceId(row);
    if (sourceId) taken.add(sourceId);
  });

  return taken;
};

/**
 * Rows that repeat a commodity / category / stock line already used by an
 * earlier row. Only the later rows are reported, so the first occurrence keeps
 * its selection and the user is asked to fix the ones that clash with it.
 */
export const duplicateStockSourceRowIndexes = (
  stockEvents: StockSourceRow[] | undefined
): number[] => {
  const seen = new Set<string>();
  const duplicates: number[] = [];

  (stockEvents || []).forEach((row, index) => {
    const sourceId = selectedStockSourceId(row);
    if (!row?.commodity || !sourceId) return;

    const key = `${row.commodity}-${rowCategory(row)}-${sourceId}`;
    if (seen.has(key)) {
      duplicates.push(index);
    } else {
      seen.add(key);
    }
  });

  return duplicates;
};

/** Handles validation for ALL form values */
const validationResolver = (
  values: any,
  {
    getWaybillOptions,
    state,
    defaultValues,
    currentSchoolEnabledPurchases,
    currentYearStore,
    previousSchoolYears,
  }: any
) => {
  const errors: {
    [fieldName: string]: {
      message: React.ReactNode;
      requiredMessage?: React.ReactNode;
    };
  } = {};
  const isSelectedDateInSchoolYearValid = schoolYearDateValidation(
    values.date,
    currentYearStore,
    previousSchoolYears
  );
  if (!isSelectedDateInSchoolYearValid && values.date) {
    errors["date"] = {
      message: ERROR_MESSAGES_INCIDENTS["invalidDate"],
    };
  }
  if (isReportClosedOrRejected(state, values.date) && values.date) {
    errors["date"] = {
      message: ERROR_MESSAGES_INCIDENTS["reportClosedOrRejected"],
    };
  }
  if (isReportApprovedOrSubmitted(state, values.date) && values.date) {
    errors["date"] = {
      message: ERROR_MESSAGES_INCIDENTS["reportApprovedOrSubmitted"],
    };
  }
  if (!values.date) {
    errors[`date`] = {
      message: ERROR_MESSAGES_INCIDENTS["missingDate"],
    };
  }
  // At least one of `causes` or `other_cause`
  if (!values.causes && !values.other_cause) {
    errors["causes"] = {
      message: ERROR_MESSAGES_INCIDENTS["causesSome"],
    };
  }
  // `commodity` is required on each stock event row
  (values.stock_events || []).forEach(
    (
      stockEvent: {
        commodity: number;
        quantity: number;
        category: string;
        waybill_no?: string;
        purchase_commodity_id?: string;
      },
      i: number
    ) => {
      if (currentSchoolEnabledPurchases && !stockEvent.category) {
        errors["category"] = {
          message: ERROR_MESSAGES_INCIDENTS["missingCategory"],
          requiredMessage: ERROR_MESSAGES_INCIDENTS["missingCategory"],
        };
      }
      if (!stockEvent.commodity) {
        errors[`stock_events[${i}].commodity`] = {
          message: ERROR_MESSAGES_INCIDENTS["missingCommodity"],
        };
      }

      if (!stockEvent.quantity) {
        errors[`stock_events[${i}].quantity`] = {
          message: ERROR_MESSAGES_INCIDENTS["missingQuantity"],
        };
      }

      if (values.reason && stockEvent.category) {
        let reasonIsIncorrect = false;
        Object.keys(values?.reason).forEach(function (key) {
          if (values.reason[key] === "2") {
            reasonIsIncorrect = true;
          }
        });

        if (
          reasonIsIncorrect &&
          stockEvent.category === purchaseDetailCategory
        ) {
          errors["reason"] = {
            message: ERROR_MESSAGES_INCIDENTS["incorrectReason"],
          };
        }
      }

      // `waybill` is required on each stock event row
      if (
        !stockEvent.waybill_no &&
        (stockEvent.category === deliveryCategory ||
          (!currentSchoolEnabledPurchases &&
            stockEvent.category !== purchaseDetailCategory))
      ) {
        errors[`stock_events[${i}].waybill_no`] = {
          message: ERROR_MESSAGES_INCIDENTS["missingWaybill"],
        };
      }
      if (
        !stockEvent.purchase_commodity_id &&
        stockEvent.category === purchaseDetailCategory
      ) {
        errors[`stock_events[${i}].purchase_commodity_id`] = {
          message: ERROR_MESSAGES_INCIDENTS["missingWaybill"],
        };
      }

      // Global admins may record incident quantities that exceed the selected
      // waybill/purchase stock; for everyone else the stock is still the cap.
      const isGlobalAdmin = getIsAdmin(getCurrentUser(state));

      if (!isGlobalAdmin) {
        let waybillStock =
          parseFloat(
            getWaybillOptions(i).find(
              (item: { value: string }) =>
                item.value &&
                item.value?.toString() === stockEvent.waybill_no?.toString()
            )?.stock
          ) || undefined;

        const defaultStockEvents = defaultValues?.stock_events;
        if (
          waybillStock !== undefined &&
          stockEvent.commodity &&
          defaultStockEvents &&
          defaultStockEvents[i] !== undefined &&
          stockEvent.commodity === defaultStockEvents[i].commodity &&
          stockEvent.waybill_no === defaultStockEvents[i].waybill_no &&
          defaultStockEvents[i].quantity !== undefined
        ) {
          waybillStock += parseFloat(defaultStockEvents[i].quantity);
        }

        if (parseFloat(String(stockEvent.quantity)) > waybillStock!) {
          errors[`stock_events[${i}].quantity`] = {
            message: ERROR_MESSAGES_INCIDENTS["insufficientStock"],
          };
        }
      }
    }
  );

  // The dropdowns already hide taken waybills / purchases, this is the net that
  // catches rows whose selection became a duplicate afterwards (for instance by
  // switching a row back to a commodity a sibling row is already reporting on).
  duplicateStockSourceRowIndexes(values.stock_events).forEach((i) => {
    const category = values.stock_events[i].category || deliveryCategory;
    errors[`stock_events[${i}].${stockSourceFieldName(category)}`] = {
      message:
        category === purchaseDetailCategory
          ? ERROR_MESSAGES_INCIDENTS["duplicatePurchase"]
          : ERROR_MESSAGES_INCIDENTS["duplicateWaybill"],
    };
  });

  if (!values.reason) {
    errors["reason"] = {
      message: ERROR_MESSAGES_INCIDENTS["missingIncidentType"],
    };
  }
  return {
    values: !isEmpty(errors) ? {} : values,
    errors,
  };
};

type FormStockEvent = {
  commodity?: number;
  quantity?: string;
  category?: string;
  measure_unit?: MeasureUnit | number;
  picture?: string;
  waybill_no?: string;
  purchase_commodity_id?: string;
  issues?: number[];
  meta?: Record<string, any>;
  side_effects?: NewStockEvent["side_effects"];
};

const targetFromSideEffects = (
  stockEvent: NewStockEvent,
  type: "DEL" | "PUR"
): number | undefined =>
  stockEvent.side_effects?.find((e) => e.related_event_type === type)
    ?.related_event_id;

const storeToFormValues = (
  allStores: Store[],
  store: IncidentEvent,
  vendorCategories: string[]
) => {
  const values: any = JSON.parse(JSON.stringify(store)); // deep copy
  const category = values.category;
  // Substitute `occurred_at` (a datetime) with `date`
  const date = moment(values.occurred_at).format("YYYY-MM-DD");
  delete values.occurred_at;

  // Convert causes from an array of ints (fks) to an integer by taking the first value
  // The array of ints should only have one value
  const storeCauses = store.causes || [];
  const causes = storeCauses.length > 0 ? storeCauses[0].toString() : "";

  values.stock_events = (values.stock_events || []).map(
    (stockEvent: NewStockEvent): FormStockEvent => {
      const newStockEvent: FormStockEvent = {
        ...stockEvent,
      };
      if (
        stockEvent.category === deliveryCategory ||
        stockEvent.category === takeHomeRationCategory
      ) {
        const deliveryID = targetFromSideEffects(stockEvent, "DEL");
        newStockEvent.waybill_no = deliveryID?.toString();
      } else if (stockEvent.category === purchaseDetailCategory) {
        const purchaseId = targetFromSideEffects(stockEvent, "PUR");
        newStockEvent.purchase_commodity_id = purchaseId?.toString();
      }
      return newStockEvent;
    }
  );

  const reasonValue = store.meta?.reason;
  const reason = reasonValue
    ? { label: incidentTypes[reasonValue], value: reasonValue }
    : undefined;
  const other_cause = store.meta?.other_cause || "";

  return { ...values, causes, date, reason, other_cause, category };
};

type RawFormStockEvent = {
  commodity?: number;
  quantity?: string;
  category: string;
  measure_unit?: MeasureUnit | number;
  picture?: string;
  waybill_no?: string | { value: string };
  purchase_commodity_id?: string | { value: string };
  issues?: number[];
  meta?: Record<string, any>;
};

type RawFormValues = {
  category: string;
  date?: string;
  causes: string;
  other_cause?: string;
  stock_events: RawFormStockEvent[];
  reason: { value: string } | string;
};

const formValuesToStore = (
  values: RawFormValues,
  incidentCauses: { id: number }[],
  getValues: () => Record<string, { category: string }[]>,
  currentStoreData: IncidentEvent | undefined
) => {
  // Substitute `date` with `occurred_at` (datetime)
  const occurred_at = moment(values.date).format("YYYY-MM-DD");
  delete values.date;

  // Convert causes from an integer to an array of one integer
  // (the causes are stored as a list on a backend, but there should only be one cause)
  const causes = incidentCauses
    .map((cause) => cause.id)
    .filter((cause) => cause === parseInt(values.causes));

  const reasonValue = extractValue(values.reason) || "";

  const category = getCategory(getValues, "stock_events");
  const stock_events: NewStockEvent[] = (values.stock_events || []).map(
    (stockEvent) => {
      const commodityCategory = stockEvent.category || deliveryCategory;

      const sideEffectTargetType: "DEL" | "PUR" =
        commodityCategory === purchaseDetailCategory ? "PUR" : "DEL";
      const targetId =
        sideEffectTargetType === "DEL"
          ? extractValue(stockEvent.waybill_no)
          : extractValue(stockEvent.purchase_commodity_id);

      const side_effects: NewStockEvent["side_effects"] = targetId
        ? [
            {
              id: 0,
              related_event_id: parseInt(targetId.toString()),
              related_event_type: sideEffectTargetType,
              chain_direction: "affects",
              qty_requested: stockEvent.quantity,
              qty_fulfilled: stockEvent.quantity,
              partial: false,
            },
          ]
        : undefined;

      const measureUnit =
        stockEvent.measure_unit && typeof stockEvent.measure_unit === "object"
          ? stockEvent.measure_unit.id
          : stockEvent.measure_unit;

      return {
        commodity: stockEvent.commodity,
        quantity: (stockEvent.quantity ?? "").toString(),
        category: commodityCategory,
        measure_unit: measureUnit,
        picture: stockEvent.picture,
        issues: stockEvent.issues,
        sign: -1,
        type: "INC",
        target: sideEffectTargetType,
        side_effects,
        meta: { ...(stockEvent.meta || {}), category: commodityCategory },
      };
    }
  );

  const meta = {
    reason: reasonValue,
    other_cause: values.other_cause || "",
    is_initial_stock_incident:
      currentStoreData?.meta?.is_initial_stock_incident || false,
  };

  return {
    occurred_at,
    causes,
    stock_events,
    meta,
    category,
  };
};

const deliveryOptions = (
  allStores: Store[],
  commodityId: number,
  category: string,
  deliveryCommodityId?: string,
  includeOutOfStock = false
) => {
  return commodityId
    ? deliveriesInStockByCommodityByCategory(
        allStores,
        commodityId,
        category,
        deliveryCommodityId,
        undefined,
        undefined,
        includeOutOfStock
      )
    : [];
};

const commodityCategoryId = (commodityId: number, category: string) =>
  `${commodityId}-${category}`;

/**
 * Identity of an entry of the delivery-options slice: the delivery / purchase
 * line id for resolved options, falling back to the store itself for the
 * initial-stock case, where the whole store event is used as the option.
 */
const stockSourceOptionId = (option: Store) => {
  const { objectCommodityId } = option as Store & {
    objectCommodityId?: number;
  };
  return objectCommodityId === undefined
    ? `store-${option.client_id}`
    : `line-${objectCommodityId}`;
};

const SingleValue = ({ children, ...props }: any) => {
  const value = props?.data?.singleValueStock;
  const measureUnit = props.data?.singleValueMeasureUnit;
  return (
    <components.SingleValue {...props}>
      {children}
      <div className="commodity-select__stock stock-value">
        {!isNaN(value) && measureUnit && (
          <>
            <FormattedMessage
              id="IncidentEdit.availableQuantity"
              defaultMessage="Available quantity: "
            />
            <MeasureUnitValue measureUnit={measureUnit}>
              {value}
            </MeasureUnitValue>
          </>
        )}
      </div>
    </components.SingleValue>
  );
};

const OptionLabel = ({
  option,
}: {
  option: {
    label: string;
    stock: number;
    measure_unit: MeasureUnit;
  };
}) => (
  <>
    {option.label}
    {option.stock && (
      <>
        {" "}
        -{" "}
        <MeasureUnitValue measureUnit={option.measure_unit}>
          {option.stock}
        </MeasureUnitValue>
      </>
    )}
  </>
);

type IncidentEditProps = {
  currentStoreData: IncidentEvent;
};

const IncidentEdit: React.ComponentType<IncidentEditProps> = ({
  currentStoreData,
}) => {
  const { updateStore, closeModal } = useStore();
  const dispatch = useDispatch();
  const submitBtnRef = useRef();
  const [incidentType, setIncidentType] = useState(
    currentStoreData?.meta?.reason
  );
  const incidentCauses = useSelector(incidentCausesDuck.getList);

  const currentYearStore = useSelector((state: RootState) =>
    getCurrentSchoolYear(state)
  );
  const previousSchoolYears = useSelector(getPreviousSchoolYears);
  const currentSchoolYearDate = currentSchoolYearDateValidation(
    currentYearStore!,
    previousSchoolYears
  );
  const state = useSelector((state) => state) as RootState;
  const isGlobalAdmin = getIsAdmin(getCurrentUser(state));
  const allStores = useSelector(getAllStores);
  const totalDeliveries = useSelector(getDeliveries);
  const [selectedCommodityId, setSelectedCommodityId] = useState<
    { commodityId: string | number; category: string } | undefined
  >(undefined);
  const vendorCategories = useSelector(getVendorCategories);
  const currentSchool = useSelector(getCurrentSchoolProfile);
  const stockStatus = useSelector(
    getStockStatus(moment().format("YYYY-MM-DD"))
  );
  const commoditiesListWfpFromSchoolProfile = useSelector(
    getCurrentSchoolProfileWfpCommodities
  );
  const commoditiesListFromSchoolProfile = useSelector(
    getCurrentSchoolProfileCommodities
  );
  const allCommoditySources: CommoditySource[] =
    useSelector(getAllCommoditySources) || [];

  const deliveryInStock = commoditiesListWfpFromSchoolProfile.some(
    (commodity: { id: number }) =>
      Number(stockStatus.delivery[commodity.id] || "0") > 0
  );
  // TODO figure out if we need to only check wfp commodities or if we can actualy just show thr if there is any thr commodity present.
  // There are thr wfp commodities that have Nonzero values.
  const thrInStock = commoditiesListWfpFromSchoolProfile.some(
    (commodity: { id: number }) =>
      Number(stockStatus.takehomeration[commodity.id] || "0") > 0
  );
  // There are purchasedetail wfp commodities that have Nonzero values.
  const purchaseInStock = commoditiesListFromSchoolProfile.some(
    (commodity: { id: number }) =>
      Number(stockStatus.purchasedetail[commodity.id] || "0") > 0
  );

  const currentSchoolEnabledPurchases =
    purchaseInStock && currentSchool.enable_cash_purchases;

  const currentSchoolEnabledTHR =
    thrInStock && currentSchool.enable_take_home_rations;
  const maxOtherCauseCharacters = 200;
  const categoryWatch = (category: string, index: number) =>
    watch(`stock_events[${index}].category`) === category;
  // A waybill / purchase only ever belongs to one commodity and one category,
  // so a row that switches either of them must drop the line it pointed at.
  const clearStockSourceSelection = (index: number) => {
    setValue(`stock_events[${index}].waybill_no`, undefined);
    setValue(`stock_events[${index}].purchase_commodity_id`, undefined);
  };
  // Use refresh key to force the waybill selects
  const [refreshKey, setRefreshKey] = useState(new Date().valueOf());

  const categoryOptions = useMemo(
    () => {
      if (allCommoditySources.length === 0) {
        return [];
      }

      const allCommoditySourcesMap: {
        [categoryCode: string]: CommoditySource;
      } = {};
      for (let source of allCommoditySources) {
        allCommoditySourcesMap[source.code] = source;
      }

      let categoryOptionCodes = [];
      const existingStockEvents = currentStoreData?.stock_events || [];
      if (
        deliveryInStock ||
        existingStockEvents.find((item) => item.category === deliveryCategory)
      ) {
        categoryOptionCodes.push(deliveryCategory);
      }
      if (
        currentSchoolEnabledPurchases ||
        existingStockEvents.find(
          (item) => item.category === purchaseDetailCategory
        )
      ) {
        categoryOptionCodes.push(purchaseDetailCategory);
      }
      if (
        currentSchoolEnabledTHR ||
        existingStockEvents.find(
          (item) => item.category === takeHomeRationCategory
        )
      ) {
        categoryOptionCodes.push(takeHomeRationCategory);
      }

      const commoditySources = categoryOptionCodes.map(
        (code: string) => allCommoditySourcesMap[code]
      );
      commoditySources.sort((a, b) => a.order - b.order);

      const categoryOptions = commoditySources.map(
        (commoditySource: CommoditySource) => ({
          value: commoditySource.code,
          name: commoditySource.code,
          label: commoditySource.name,
        })
      );
      return categoryOptions;
    },

    // eslint-disable-next-line react-hooks/exhaustive-deps
    [allCommoditySources.length]
  );

  // Get Options for all pre-filled commodities when "editing".
  useEffect(() => {
    if (!currentStoreData) return;
    const currentDataStockEvents = currentStoreData.stock_events || [];
    // Rows sharing a commodity and a category share one options key, so the
    // options resolved for each of them have to be merged under that key
    // instead of overwriting one another - otherwise only the last row keeps
    // its pre-selected waybill.
    const optionsByCommodityCategory: { [key: string]: Store[] } = {};
    const collectOptions = (key: string, options: Store[]) => {
      const collected = optionsByCommodityCategory[key] || [];
      optionsByCommodityCategory[key] = collected.concat(
        options.filter(
          (option) =>
            !collected.some(
              (item) =>
                stockSourceOptionId(item) === stockSourceOptionId(option)
            )
        )
      );
    };

    currentDataStockEvents.forEach((stockEvent) => {
      const category = stockEvent.category || deliveryCategory;
      const deliveryTarget = targetFromSideEffects(stockEvent, "DEL");
      const purchaseTarget = targetFromSideEffects(stockEvent, "PUR");
      const targetId =
        category === deliveryCategory || category === takeHomeRationCategory
          ? deliveryTarget
          : purchaseTarget;
      const options = deliveryOptions(
        allStores,
        parseInt(stockEvent?.commodity?.toString() || "0"),
        category,
        targetId?.toString(),
        isGlobalAdmin
      );
      const commodityCategory = currentSchoolEnabledPurchases
        ? commodityCategoryId(stockEvent?.commodity ?? 0, category)
        : commodityCategoryId(stockEvent?.commodity ?? 0, deliveryCategory);

      if (options?.length > 0) {
        collectOptions(commodityCategory, options);
      } else {
        const delivery = getDeliveryByCommodityAndDeliveryID(
          allStores,
          targetId as number
        );
        const purchase = getPurchaseByCommodityPurchaseID(
          allStores,
          targetId as number
        );
        const commodityDeliveryOrPurchase = currentSchoolEnabledPurchases
          ? category === deliveryCategory
            ? delivery
            : purchase
          : delivery;

        if (!commodityDeliveryOrPurchase) return;

        collectOptions(commodityCategory, [commodityDeliveryOrPurchase]);
      }
    });

    if (!isEmpty(optionsByCommodityCategory)) {
      dispatch(setDeliveries(optionsByCommodityCategory));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allStores, currentStoreData]);

  // TO-DO: "mixed" delivery category
  const getWaybillOptions = (index: number) => {
    const indexCommodityID = getValues(`stock_events[${index}].commodity`);
    const indexCategory =
      currentSchoolEnabledPurchases || currentSchoolEnabledTHR
        ? getValues(`stock_events[${index}].category`)
        : deliveryCategory;
    const commodityCategoryId = `${indexCommodityID}-${indexCategory}`;
    return (totalDeliveries?.hasOwnProperty(commodityCategoryId)
      ? totalDeliveries[commodityCategoryId]
      : []
    ).map((option: any) => {
      const waybillLabel = `${option.waybill_no}: ${moment(
        option.occurred_at
      ).format("dddd, DD.MM.YYYY")}`;
      const vendorCategoryName = getSelectedVendorName(
        option.vendor_category,
        vendorCategories
      );
      const purchaseLabel = `${vendorCategoryName}: ${moment(
        option.occurred_at
      ).format("dddd, DD.MM.YYYY")}`;

      return {
        label:
          indexCategory === deliveryCategory ||
          indexCategory === takeHomeRationCategory
            ? waybillLabel
            : purchaseLabel,
        value: option.objectCommodityId,
        date: moment(option.occurred_at).format("YYYY-MM-DD"),
        sync: option.sync,
        consumptionsUnsynced: option.consumptionsUnsynced,
        stock: option.quantityAvailable,
        measure_unit: option.measure_unit,
      };
    });
  };

  const methods = useForm({
    resolver: validationResolver,
    defaultValues: currentStoreData
      ? storeToFormValues(allStores, currentStoreData, vendorCategories)
      : { stock_events: [makeNewCommodity(categoryOptions[0]?.value)] },
    context: {
      getWaybillOptions,
      state,
      currentSchoolEnabledPurchases,
      currentYearStore,
      previousSchoolYears,
      defaultValues: currentStoreData
        ? storeToFormValues(allStores, currentStoreData, vendorCategories)
        : { stock_events: [{}] },
    },
  });
  const {
    control,
    handleSubmit,
    errors,
    getValues,
    watch,
    setValue,
    register,
    reset,
  } = methods;

  const { fields, append, remove } = useFieldArray({
    control,
    name: "stock_events",
  });

  const selectedDate = watch("date");
  const isInitialStockIncident =
    currentStoreData?.meta?.is_initial_stock_incident;
  const isSelectedDateInSchoolYearValid = schoolYearDateValidation(
    selectedDate,
    currentYearStore!,
    previousSchoolYears
  );
  const isErrorListEmpty = isEmpty(errors);
  const isCommoditySelected = watch("stock_events").find(
    (i: any) => i.commodity !== undefined
  );
  const onlyIncidentOnDate = useSelector(
    checkIncidentDateIsUniquePerDate(
      moment(selectedDate).format("YYYY-MM-DD"),
      currentStoreData
    )
  );

  const isAllCommoditySourcesFetching = useSelector(
    getAllCommoditySourcesFetching
  );

  const isOffline = useSelector(getLoggedInOffline);

  const isIncidentUnique = isInitialStockIncident ? true : onlyIncidentOnDate;

  let prevDate = useRef();

  // When selecting commodity, get the available delivery options and send them to redux store.
  // This allows for the page to be correctly rerendered without loosing the delivery options for each commodity
  useEffect(() => {
    if (selectedCommodityId) {
      const commodityCategory = commodityCategoryId(
        selectedCommodityId.commodityId as number,
        selectedCommodityId.category
      );

      const options = deliveryOptions(
        allStores,
        selectedCommodityId.commodityId as number,
        selectedCommodityId.category,
        undefined,
        isGlobalAdmin
      );
      if (options.length > 0) {
        dispatch(setDeliveries({ [commodityCategory]: options }));
      }
    }
  }, [allStores, dispatch, selectedCommodityId, selectedDate, isGlobalAdmin]);

  useEffect(() => {
    if (!prevDate.current ? (prevDate.current = selectedDate) : prevDate) {
      if (prevDate.current !== selectedDate) {
        setSelectedCommodityId(undefined);
        dispatch(clearDeliveryOptions());
        setValue("stock_events", []);
        reset({
          date: selectedDate,
          stock_events: [makeNewCommodity(categoryOptions[0]?.value)],
        });
        prevDate.current = selectedDate;
        setRefreshKey(new Date().valueOf());
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate, setValue, reset]);
  const id =
    currentStoreData && currentStoreData.client_id
      ? currentStoreData.client_id
      : undefined;

  const onSubmit = (values: any) => {
    dispatch(clearDeliveryOptions());
    updateStore({
      id: id,
      values: {
        ...formValuesToStore(
          values,
          incidentCauses,
          getValues,
          currentStoreData
        ),
        event_type: "incident",
      },
      type: id ? "update" : "create",
      category: getCategory(getValues, "stock_events"),
    });
  };

  const submitViaBtnRefClick = () => {
    (submitBtnRef.current as any).click();
  };

  const customStyles = (invalid: boolean) => ({
    option: (provided: any, state: any) => {
      return {
        ...provided,
        color:
          state.data?.stock &&
          parseFloat(state.data?.stock) > 0 &&
          !state.isSelected
            ? "#689e18"
            : "inherit",
      };
    },
    control: (styles: any) =>
      invalid
        ? selectContainerStyles.invalid.control(styles)
        : selectContainerStyles.valid.control(styles),
  });

  useEffect(() => {
    if (!isOffline) {
      dispatch(requestAllCommoditySources());
    }
  }, [dispatch, isOffline]);

  return (
    <ModalExtended
      hideClose
      onRequestSubmit={submitViaBtnRefClick}
      onRequestClose={() => {}}
      onSecondarySubmit={() => closeModal()}
      modalLabel={
        <FormattedMessage
          id="IncidentEdit.modalTitle"
          defaultMessage="Reporting"
        />
      }
      modalHeading={
        currentStoreData?.meta?.is_initial_stock_incident ? (
          <FormattedMessage
            id="IncidentEdit.modalSubtitleInitialStockIncident"
            defaultMessage="Report initial stock incident"
          />
        ) : (
          <FormattedMessage
            id="IncidentEdit.modalSubtitle"
            defaultMessage="Report incident"
          />
        )
      }
      primaryButtonText={
        <FormattedMessage
          id="IncidentEdit.saveIncident"
          defaultMessage="Save incident"
        />
      }
      primaryButtonDisabled={!isIncidentUnique}
      className="incidents-edit"
    >
      {isAllCommoditySourcesFetching ? (
        <InlineLoading />
      ) : (
        <FormProvider {...methods}>
          {!isIncidentUnique && (
            <Blockquote kind="warning" className="not-unique-error">
              <p>
                <FormattedMessage
                  id="errorGeneric.incidentDateUnique"
                  defaultMessage="You can only create one incident per day. To add information for this day, please edit the existing incident."
                />
              </p>
            </Blockquote>
          )}
          {!isSelectedDateInSchoolYearValid && isErrorListEmpty && (
            <Blockquote kind="warning" className="date-not-valid">
              {ERROR_MESSAGES_INCIDENTS.invalidDate}
            </Blockquote>
          )}
          {isReportClosedOrRejected(state, selectedDate) && isErrorListEmpty && (
            <Blockquote kind="warning" className="date-not-valid">
              {ERROR_MESSAGES_INCIDENTS.reportClosedOrRejected}
            </Blockquote>
          )}
          {isReportApprovedOrSubmitted(state, selectedDate) &&
            isErrorListEmpty && (
              <Blockquote kind="warning" className="date-not-valid">
                {ERROR_MESSAGES_INCIDENTS.reportApprovedOrSubmitted}
              </Blockquote>
            )}
          {!selectedDate && !isCommoditySelected && (
            <Blockquote kind="warning" className="not-unique-error">
              <p>
                <FormattedMessage
                  id="errorGeneric.selectedDateCommodity"
                  defaultMessage="You need to select a date to see commodities options."
                />
              </p>
            </Blockquote>
          )}
          {!selectedDate && isCommoditySelected && (
            <Blockquote kind="warning" className="not-unique-error">
              <p>
                <FormattedMessage
                  id="errorGeneric.selectedDateWaybill"
                  defaultMessage="You need to select a date to see waybill options."
                />
              </p>
            </Blockquote>
          )}

          <ErrorList errors={errors} />

          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="wfp--form wfp--form-long">
              <div className="wfp--form-item">
                <Controller
                  control={control}
                  render={({ onChange, value }) => (
                    <Camera onChange={onChange} image={value} />
                  )}
                  name={"picture"}
                />
              </div>
              <div className="wfp--form-item wfp--form-item--invalid date-input">
                <Controller
                  as={
                    <DateInput
                      labelText={
                        <FormattedMessage
                          id="IncidentEdit.incidentDate"
                          defaultMessage="Incident date"
                        />
                      }
                      min={currentSchoolYearDate.min}
                      max={currentSchoolYearDate.max}
                    />
                  }
                  rules={{
                    required: true,
                  }}
                  invalid={errors.date}
                  invalidText={errors.date && errors.date.message}
                  name="date"
                  control={control}
                />
              </div>
              <Controller
                render={({ onChange, onBlur, value }) => {
                  return (
                    <Input
                      formItemClassName={classnames(
                        "wfp--form-item",
                        "wfp--react-select-container"
                      )}
                      labelText={
                        <FormattedMessage
                          id="IncidentEdit.incidentType"
                          defaultMessage="Incident Type"
                        />
                      }
                      name={"reason"}
                      invalid={errors["reason"] !== undefined}
                      invalidText={
                        <FormattedMessage
                          id="IncidentEditError.incidentType"
                          defaultMessage="Enter a incident type"
                        />
                      }
                    >
                      {() => (
                        <div className="commodity-select__input">
                          <Select
                            className="wfp--react-select-container"
                            classNamePrefix="wfp--react-select"
                            key={"reason"}
                            styles={customStyles(
                              errors["reason"] !== undefined
                            )}
                            onChange={(e: { value: string }) => {
                              setIncidentType(e.value);
                              onChange(e.value);
                            }}
                            onBlur={onBlur}
                            defaultValue={value}
                            options={Object.keys(incidentTypes).map((key) => ({
                              label: incidentTypes[key],
                              value: key,
                            }))}
                          />
                        </div>
                      )}
                    </Input>
                  );
                }}
                name={"reason"}
              />
              {incidentType && (
                <RadioButtonGroup
                  labelText={
                    <FormattedMessage
                      id="IncidentEdit.Reason"
                      defaultMessage="Reason"
                    />
                  }
                >
                  {incidentCauses.map((cause) => {
                    if (
                      cause.reason === parseInt(incidentType) ||
                      cause.reason === 3
                    ) {
                      return (
                        <RadioButton
                          className="cause-radio-button"
                          key={cause.id}
                          labelText={cause.name}
                          id={cause.id}
                          name={`causes`}
                          value={cause.id}
                          ref={register({ required: true })}
                        />
                      );
                    } else {
                      return <></>;
                    }
                  })}
                </RadioButtonGroup>
              )}

              <Controller
                as={
                  <TextInput
                    labelText={
                      <FormattedMessage
                        id="IncidentEdit.other_cause"
                        defaultMessage="Other reason"
                      />
                    }
                    maxLength={maxOtherCauseCharacters}
                  />
                }
                name={"other_cause"}
                control={control}
              />
            </div>

            <>
              <br />
              <div>
                {fields.length === 0 && (
                  <Empty
                    title="No commodities added yet"
                    icon={<FontAwesomeIcon icon={faWheatAwn} size="1x" />}
                  >
                    <FormattedMessage
                      id="IncidentEdit.addaCommodityWhichIsAffectedByTheIncident"
                      defaultMessage="Add a commodity which is affected by the incident"
                    />
                  </Empty>
                )}
                {fields.map((field, index) => {
                  const rowOptions = getWaybillOptions(index);
                  // The delivery select only offers waybills received on or
                  // before the incident date, purchases are not date bound.
                  const deliveryOptionsForRow = rowOptions.filter(
                    (option: { date: string }) =>
                      moment(option.date).isSameOrBefore(moment(selectedDate))
                  );
                  const takenSourceIds = stockSourceIdsTakenByOtherRows(
                    getValues()["stock_events"],
                    index
                  );
                  const isSourceFree = (option: { value: string | number }) =>
                    !takenSourceIds.has(option.value?.toString());
                  const selectableDeliveryOptions = deliveryOptionsForRow.filter(
                    isSourceFree
                  );
                  const selectablePurchaseOptions = rowOptions.filter(
                    isSourceFree
                  );

                  return (
                    <RepeaterItem
                      key={field.id}
                      disableRemove={fields.length === 1}
                      removeAction={() => remove(index)}
                      extraTopRightChild={
                        <Controller
                          control={control}
                          render={({ onChange, value }) => (
                            <Camera onChange={onChange} image={value} />
                          )}
                          name={`stock_events[${index}].picture`}
                        />
                      }
                    >
                      <Controller
                        render={({ onChange, value }) => (
                          <div
                            className={
                              categoryOptions.length > 1
                                ? "container"
                                : "disabled"
                            }
                          >
                            <RadioButtonGroup
                              labelText={
                                <FormattedMessage
                                  id="MealsEdit.commoditySource"
                                  defaultMessage="Commodity Source"
                                />
                              }
                            >
                              {categoryOptions.map((category) => (
                                <RadioButton
                                  data-testid="category-radio-button"
                                  key={category.value}
                                  labelText={category.label}
                                  id={`stock_events[${index}][${category.value}]`}
                                  name={`stock_events[${index}].category`}
                                  value={category.value}
                                  checked={category.value === value}
                                  onChange={(
                                    event: React.ChangeEvent<HTMLInputElement>,
                                    selectedCategory: string
                                  ) => {
                                    if (selectedCategory !== value) {
                                      clearStockSourceSelection(index);
                                    }
                                    onChange(event);
                                  }}
                                />
                              ))}
                            </RadioButtonGroup>
                          </div>
                        )}
                        control={control}
                        rules={{
                          required: categoryOptions.length > 1,
                        }}
                        name={`stock_events[${index}].category`}
                      />
                      <Controller
                        render={({ onChange, value }) => (
                          <CommoditySelect
                            isDisabled={!selectedDate ? true : false}
                            showStock={true}
                            date={selectedDate}
                            key={`stock_events[${index}].commodity`}
                            category={
                              currentSchoolEnabledPurchases ||
                              currentSchoolEnabledTHR
                                ? watch(`stock_events[${index}].category`)
                                : deliveryCategory
                            }
                            onChange={(e: any) => {
                              // `CommoditySelect` also fires this when it
                              // auto-selects its only option, so the previously
                              // linked waybill may only be dropped when the
                              // commodity really changes.
                              if (value?.toString() !== e.value?.toString()) {
                                clearStockSourceSelection(index);
                              }
                              setValue(
                                `stock_events[${index}].measure_unit`,
                                e.measure_unit
                              );
                              onChange(e.value);

                              setSelectedCommodityId({
                                commodityId: parseInt(e.value),
                                category:
                                  watch(`stock_events[${index}].category`) ||
                                  categoryOptions[0]?.value,
                              });

                              setRefreshKey(new Date().valueOf());
                            }}
                            value={value}
                            invalidOverride={
                              errors[`stock_events[${index}].commodity`] !==
                              undefined
                            }
                            invalidTextOverride={
                              ERROR_MESSAGES_INCIDENTS["missingCommodity"]
                            }
                          />
                        )}
                        name={`stock_events[${index}].commodity`}
                        control={control}
                      />
                      {((!currentSchoolEnabledPurchases &&
                        !categoryWatch(purchaseDetailCategory, index)) ||
                        categoryWatch(deliveryCategory, index) ||
                        categoryWatch(takeHomeRationCategory, index)) && (
                        <div className="waybill-select">
                          <Controller
                            key={refreshKey}
                            render={({ onChange, onBlur, value }) => {
                              const waybillOption =
                                value != null
                                  ? selectableDeliveryOptions.find(
                                      (item: any) =>
                                        item.value.toString() ===
                                        value.toString()
                                    )
                                  : undefined;

                              const fieldValue = waybillOption
                                ? {
                                    value: waybillOption?.value,
                                    label: waybillOption?.label,
                                    singleValueStock: waybillOption?.stock,
                                  }
                                : undefined;
                              return (
                                <Input
                                  formItemClassName={classnames(
                                    "wfp--form-item commodity-select",
                                    "wfp--react-select-container"
                                  )}
                                  labelText={
                                    <FormattedMessage
                                      id="IncidentEdit.selectWaybill"
                                      defaultMessage="Waybill"
                                    />
                                  }
                                  name={`stock_events[${index}].waybill_no`}
                                  invalid={
                                    errors[
                                      `stock_events[${index}].waybill_no`
                                    ] !== undefined
                                  }
                                  invalidText={
                                    <FormattedMessage
                                      id="IncidentEditError.missingWaybillStar"
                                      defaultMessage="Enter a waybill number"
                                    />
                                  }
                                >
                                  {() => (
                                    <div className="commodity-select__input ">
                                      <Select
                                        className="wfp--react-select-container"
                                        classNamePrefix="wfp--react-select"
                                        key={`stock_events[${index}].waybill_no`}
                                        styles={customStyles(
                                          errors[
                                            `stock_events[${index}].waybill_no`
                                          ] !== undefined
                                        )}
                                        onChange={(e: any) => {
                                          onChange(e.value);
                                        }}
                                        isDisabled={
                                          !selectedDate ||
                                          !isCommoditySelected ||
                                          (deliveryOptionsForRow.find(
                                            (e: any) => e.sync === false
                                          )
                                            ? true
                                            : false) ||
                                          deliveryOptionsForRow.find(
                                            (e: any) =>
                                              e.consumptionsUnsynced === true
                                          )
                                            ? true
                                            : false
                                        }
                                        style={{
                                          opacity:
                                            !selectedDate ||
                                            !isCommoditySelected
                                              ? "0.5"
                                              : "",
                                        }}
                                        onBlur={onBlur}
                                        value={fieldValue}
                                        components={{ SingleValue }}
                                        options={selectableDeliveryOptions}
                                        getOptionLabel={(option: {
                                          label: string;
                                          stock: number;
                                          measure_unit: MeasureUnit;
                                        }) => <OptionLabel option={option} />}
                                      />
                                    </div>
                                  )}
                                </Input>
                              );
                            }}
                            name={`stock_events[${index}].waybill_no`}
                            control={control}
                          />
                          {deliveryOptionsForRow.length > 0 &&
                            selectableDeliveryOptions.length === 0 && (
                              <Blockquote kind="warning">
                                <p>
                                  <FormattedMessage
                                    id="IncidentEdit.allWaybillsAlreadyLinked"
                                    defaultMessage="Every waybill of this commodity is already linked in another row. Remove this row or change its commodity."
                                  />
                                </p>
                              </Blockquote>
                            )}
                          {rowOptions.find(
                            (e: { sync: boolean }) => e.sync === false
                          ) && (
                            <Blockquote kind="warning">
                              <p>
                                <FormattedMessage
                                  id="IncidentEdit.PleaseSyncDeliveries"
                                  defaultMessage="Please sync deliveries before linking an incident"
                                />
                              </p>
                            </Blockquote>
                          )}
                          {rowOptions.find(
                            (e: { consumptionsUnsynced: boolean }) => {
                              return e.consumptionsUnsynced === true;
                            }
                          ) && (
                            <Blockquote kind="warning">
                              <p>
                                <FormattedMessage
                                  id="IncidentEdit.PleaseSyncConsumptions"
                                  defaultMessage="Please sync consumptions before linking an incident"
                                />
                              </p>
                            </Blockquote>
                          )}
                        </div>
                      )}
                      {categoryWatch(purchaseDetailCategory, index) && (
                        <div className="waybill-select">
                          <Controller
                            key={refreshKey}
                            render={({ onChange, onBlur, value }) => {
                              const purchaseOption =
                                value != null
                                  ? selectablePurchaseOptions.find(
                                      (item: { value: string }) =>
                                        item.value.toString() ===
                                        value.toString()
                                    )
                                  : undefined;

                              const fieldValue = purchaseOption
                                ? {
                                    value: purchaseOption?.value,
                                    label: purchaseOption?.label,
                                    singleValueStock: purchaseOption?.stock,
                                    singleValueMeasureUnit:
                                      purchaseOption?.measure_unit,
                                  }
                                : undefined;
                              return (
                                <Input
                                  formItemClassName={classnames(
                                    "wfp--form-item commodity-select",
                                    "wfp--react-select-container"
                                  )}
                                  labelText={
                                    <FormattedMessage
                                      id="IncidentEdit.selectPurchase"
                                      defaultMessage="Purchase"
                                    />
                                  }
                                  name={`stock_events[${index}].purchase_commodity_id`}
                                  invalid={
                                    errors[
                                      `stock_events[${index}].purchase_commodity_id`
                                    ] !== undefined
                                  }
                                  invalidText={
                                    <FormattedMessage
                                      id="IncidentEditError.missingPurchaseNoStar"
                                      defaultMessage="* Enter a purchase number"
                                    />
                                  }
                                >
                                  {() => (
                                    <div className="commodity-select__input ">
                                      <Select
                                        className="wfp--react-select-container"
                                        classNamePrefix="wfp--react-select"
                                        key={`stock_events[${index}].purchase_commodity_id`}
                                        styles={customStyles(
                                          errors[
                                            `stock_events[${index}].purchase_commodity_id`
                                          ] !== undefined
                                        )}
                                        onChange={(e: { value: string }) => {
                                          onChange(e.value);
                                        }}
                                        onBlur={onBlur}
                                        value={fieldValue}
                                        components={{ SingleValue }}
                                        options={selectablePurchaseOptions}
                                        getOptionLabel={(option: {
                                          label: string;
                                          stock: number;
                                          measure_unit: MeasureUnit;
                                        }) => <OptionLabel option={option} />}
                                        isDisabled={
                                          !selectedDate ||
                                          !isCommoditySelected ||
                                          (rowOptions.find(
                                            (e: { sync: boolean }) =>
                                              e.sync === false
                                          )
                                            ? true
                                            : false) ||
                                          rowOptions.find(
                                            (e: {
                                              consumptionsUnsynced: boolean;
                                            }) =>
                                              e.consumptionsUnsynced === true
                                          )
                                            ? true
                                            : false
                                        }
                                        style={{
                                          opacity:
                                            !selectedDate ||
                                            !isCommoditySelected
                                              ? "0.5"
                                              : "",
                                        }}
                                      />
                                    </div>
                                  )}
                                </Input>
                              );
                            }}
                            name={`stock_events[${index}].purchase_commodity_id`}
                            control={control}
                          />
                          {rowOptions.length > 0 &&
                            selectablePurchaseOptions.length === 0 && (
                              <Blockquote kind="warning">
                                <p>
                                  <FormattedMessage
                                    id="IncidentEdit.allPurchasesAlreadyLinked"
                                    defaultMessage="Every purchase of this commodity is already linked in another row. Remove this row or change its commodity."
                                  />
                                </p>
                              </Blockquote>
                            )}
                          {rowOptions.find((e: { sync: boolean }) => {
                            return e.sync === false;
                          }) && (
                            <Blockquote kind="warning">
                              <p>
                                <FormattedMessage
                                  id="IncidentEdit.PleaseSyncDeliveries"
                                  defaultMessage="Please sync deliveries before linking an incident"
                                />
                              </p>
                            </Blockquote>
                          )}
                          {rowOptions.find(
                            (e: { consumptionsUnsynced: boolean }) => {
                              return e.consumptionsUnsynced === true;
                            }
                          ) && (
                            <Blockquote kind="warning">
                              <p>
                                <FormattedMessage
                                  id="IncidentEdit.PleaseSyncConsumptions"
                                  defaultMessage="Please sync consumptions before linking an incident"
                                />
                              </p>
                            </Blockquote>
                          )}
                        </div>
                      )}

                      <Controller
                        render={({ onChange, onBlur, value }) => {
                          return (
                            <QuantityInput
                              control={control}
                              value={value}
                              onChange={onChange}
                              onBlur={onBlur}
                              labelText={
                                <FormattedMessage
                                  id="DeliveriesEdit.quantity"
                                  defaultMessage="Quantity"
                                />
                              }
                              setValue={setValue}
                              name={`stock_events[${index}].quantity`}
                              invalid={
                                errors[`stock_events[${index}].quantity`]
                              }
                              invalidText={
                                errors[`stock_events[${index}].quantity`]
                                  ?.message
                              }
                              measureUnit={watch(
                                `stock_events[${index}].measure_unit`
                              )}
                            />
                          );
                        }}
                        control={control}
                        name={`stock_events[${index}].quantity`}
                      />

                      <Controller
                        name={`stock_events[${index}].measure_unit`}
                        control={control}
                      />
                    </RepeaterItem>
                  );
                })}
              </div>
              <RepeaterAddButton onClick={() => append({})} />
            </>

            <button className="hidden-btn" ref={submitBtnRef as any} />
          </form>
        </FormProvider>
      )}
    </ModalExtended>
  );
};

export default IncidentEdit;
